import type { TextFieldProps } from '@shopify/polaris';

import { TextField } from '@shopify/polaris';
import { useCallback, useRef } from 'react';

export interface GTextFieldProps extends Omit<TextFieldProps, 'autoComplete' | 'label'> {
  autoComplete?: string;
  label?: React.ReactNode;
  onEnter?: (value: string, id: string) => void;
  onTab?: (value: string, id: string) => void;
  onEscape?: (value: string, id: string) => void;
}

type KeyHandler = (value: string, id: string) => void;

const keyHandlers: Record<string, keyof Pick<GTextFieldProps, 'onEnter' | 'onTab' | 'onEscape'>> = {
  Enter: 'onEnter',
  Tab: 'onTab',
  Escape: 'onEscape',
};

export const GTextField: React.FC<GTextFieldProps> = ({
  value = '',
  label = '',
  onChange,
  onBlur,
  onEnter,
  onTab,
  onEscape,
  autoComplete = 'off',
  ...textFieldProps
}) => {
  const textFieldId = useRef<string>('');

  const handleChange = useCallback(
    (newValue: string, id: string) => {
      textFieldId.current = id;
      onChange?.(newValue, id);
    },
    [onChange],
  );

  const handleBlur = useCallback(
    (event?: React.FocusEvent) => {
      onBlur?.(event);
    },
    [onBlur],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const { key } = event;
      const handlerProp = keyHandlers[key];
      const handler = handlerProp && ({ onEnter, onTab, onEscape }[handlerProp] as KeyHandler);

      if (!handler) return;

      const trimmedValue = value.trim();
      const id = textFieldId.current;

      if (!id) return;

      onChange?.(trimmedValue, id);
      handler(trimmedValue, id);
    },
    [value, onChange, onEnter, onTab, onEscape],
  );

  return (
    // Plain wrapper (not GDiv): GDiv is a role="button" element that preventDefaults
    // Space/Enter, which swallowed spaces typed into the field. This wrapper only
    // carries the Enter/Tab/Escape handler (which never preventDefaults Space).
    <div className="GTextField" onKeyDown={handleKeyDown}>
      <TextField
        label={label}
        labelHidden={!label}
        autoComplete={autoComplete}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        {...textFieldProps}
      />
    </div>
  );
};
